import {
  ChevronDown, ChevronRight, PanelLeft, Search, LayoutDashboard, X
} from "lucide-react";
import * as LucideIcons from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "./ui/utils";
import { useState, useEffect, useMemo } from "react";
import { ScrollArea } from "./ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { NavLink, useLocation } from "react-router-dom";
import { APP_NAVIGATION, NavItem } from "./config/navigation";
import { usePermissions } from "./contexts/PermissionContext";
import { motion, AnimatePresence } from "framer-motion";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  userRole: "Superadmin" | "Admin" | "Teacher" | "Student" | "Client" | "Customer" | null;
}

export function Sidebar({
  isOpen,
  onClose,
  userRole,
}: SidebarProps) {
  const location = useLocation();
  const { allowedSlugs, loading } = usePermissions();
  const [collapsed, setCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedMenu, setExpandedMenu] = useState<string | null>(() => {
    return localStorage.getItem("sidebar-expanded");
  });

  useEffect(() => {
    if (expandedMenu) {
      localStorage.setItem("sidebar-expanded", expandedMenu);
    } else {
      localStorage.removeItem("sidebar-expanded");
    }
  }, [expandedMenu]);

  const menuItems = useMemo(() => {
    // Don't compute until permissions have resolved — prevents flash
    if (loading || allowedSlugs === null) return [];

    let base = [...APP_NAVIGATION];

    // Filter by permissions for non-superadmins
    if (userRole !== "Superadmin" && !allowedSlugs.has("ALL")) {
      base = base
        .map((item): NavItem | null => {
          const isParentAllowed = allowedSlugs.has(item.slug);

          // Filter subItems if they exist
          const filteredSubItems = item.subItems?.filter(sub => allowedSlugs.has(sub.slug));

          // An item is visible if it is directly allowed OR has at least one allowed sub-item
          if (isParentAllowed || (filteredSubItems && filteredSubItems.length > 0)) {
            return {
              ...item,
              subItems: filteredSubItems
            };
          }
          return null;
        })
        .filter((item): item is NavItem => item !== null);
    }

    if (!searchQuery.trim()) return base;
    return base.filter(item =>
      item.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.subItems?.some(sub => sub.label.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [userRole, allowedSlugs, loading, searchQuery]);

  // Auto-expand menu containing active child
  useEffect(() => {
    const activeItem = menuItems.find(item =>
      item.path === location.pathname ||
      item.subItems?.some(sub => location.pathname === sub.path)
    );
    if (activeItem && activeItem.subItems) {
      setExpandedMenu(activeItem.path);
    }
  }, [location.pathname, menuItems]);

  const resolveIcon = (name: string) => {
    const Icon = (LucideIcons as any)[name];
    return Icon || LayoutDashboard;
  };

  const isMobile = typeof window !== "undefined" && window.innerWidth < 1024;

  const springTransition = { duration: 0 };

  return (
    <>
      {/* Mobile Overlay */}
      <AnimatePresence>
        {isOpen && isMobile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      <motion.aside
        initial={false}
        animate={{
          width: collapsed && !isMobile ? 80 : 256,
          x: isOpen || !isMobile ? 0 : -256
        }}
        transition={springTransition}
        className={cn(
          "fixed lg:sticky top-0 left-0 h-screen bg-sidebar border-r border-sidebar-border z-50 flex flex-col"
        )}
      >
        {/* Mobile Header (Close Button) */}
        <div className="h-16 border-b border-sidebar-border flex items-center justify-between px-4 lg:hidden">
          <span className="font-medium text-transparent bg-clip-text bg-[var(--brand-gradient)]">XEROALPHA</span>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent rounded-xl">
            <X className="h-5 w-5 stroke-[2.5px]" />
          </Button>
        </div>

        {/* Search + Collapse Toggle (Desktop) */}
        <div className="hidden lg:flex items-center gap-2 px-4 pt-6 pb-6">
          <div className="flex-1 relative h-10">
            <AnimatePresence initial={false}>
              {!collapsed && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={springTransition}
                  className="w-full h-full"
                >
                  <div className="relative flex items-center bg-sidebar-accent/50 border border-sidebar-border rounded-lg px-3 h-full transition-all hover:bg-sidebar-accent focus-within:ring-2 focus-within:ring-primary/20">
                    <Search className="h-4 w-4 text-sidebar-foreground/60 mr-3 shrink-0 stroke-[2.5px]" />
                    <input
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="bg-transparent border-none focus:outline-none text-[13px] text-sidebar-foreground placeholder:text-sidebar-foreground/50 w-full font-medium"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            {collapsed && (
              <div className="flex items-center justify-center h-full">
                <Search className="h-5 w-5 text-sidebar-foreground/70" />
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="shrink-0 h-8 w-8 text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent rounded-lg flex items-center justify-center p-0"
          >
            <PanelLeft className="h-[18px] w-[18px] stroke-[2.2px]" />
          </Button>
        </div>

        <ScrollArea className="flex-1 overflow-y-auto px-2 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
          <TooltipProvider>
            <nav className="space-y-[6px] mt-2 lg:mt-0 pb-24">
              {loading ? (
                // ── Skeleton shimmer while permissions load ──────────────────
                <div className="space-y-2 px-2 pt-1 animate-pulse">
                  {[...Array(6)].map((_, i) => (
                    <div
                      key={i}
                      className="h-10 rounded-xl bg-sidebar-accent"
                    />
                  ))}
                </div>
              ) : (
                menuItems.map((item) => {
                  const Icon = resolveIcon(item.icon);
                  const hasSubmenu = item.subItems && item.subItems.length > 0;
                  const isExpanded = expandedMenu === item.path;
                  const isSubActive = hasSubmenu && item.subItems?.some(sub => location.pathname === sub.path);

                  const linkItem = (
                    <NavLink
                      to={item.path}
                      onClick={(e) => {
                        if (hasSubmenu) {
                          e.preventDefault();
                          if (collapsed) setCollapsed(false);
                          setExpandedMenu(isExpanded ? null : item.path);
                        } else {
                          setExpandedMenu(null);
                          if (isMobile) onClose();
                        }
                      }}
                      className={({ isActive }) => {
                        const active = isActive || isSubActive;
                        return cn(
                          "flex items-center rounded-xl h-11 relative group overflow-hidden whitespace-nowrap transition-all duration-150",
                          active
                            ? "bg-gradient-to-r from-[#A855F7] to-[#6366F1] text-white font-semibold shadow-lg shadow-purple-500/20"
                            : "text-sidebar-foreground hover:bg-violet-500/15 hover:text-violet-500 dark:hover:bg-violet-500/10 font-medium",
                          (!collapsed || isMobile)
                            ? (active ? "scale-[1.01] px-4 mx-2 justify-start" : "hover:scale-[1.01] px-4 mx-2 justify-start")
                            : "px-0 justify-center w-11 h-11 mx-auto"
                        );
                      }}
                    >
                      {({ isActive }) => {
                        const active = isActive || isSubActive;
                        return (
                          <>
                            <Icon className={cn(
                              "h-[18px] w-[18px] shrink-0 stroke-[2.2px]",
                              active ? "text-white" : "text-sidebar-foreground group-hover:text-violet-500"
                            )} />

                            <AnimatePresence mode="wait">
                              {(!collapsed || isMobile) && (
                                <motion.div
                                  key="label"
                                  initial={{ opacity: 0, x: -5 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{ opacity: 0, x: -5 }}
                                  transition={{ ...springTransition, delay: 0.05 }}
                                  className="flex items-center flex-1 ml-3 overflow-hidden"
                                >
                                  <span className="flex-1 text-[13.5px] tracking-normal truncate whitespace-nowrap">
                                    {item.label}
                                  </span>
                                  {hasSubmenu && (
                                    <motion.div
                                      animate={{ rotate: isExpanded ? 90 : 0 }}
                                      transition={springTransition}
                                      className="ml-auto shrink-0 opacity-80"
                                    >
                                      <ChevronRight className="h-3.5 w-3.5" />
                                    </motion.div>
                                  )}
                                </motion.div>
                              )}
                            </AnimatePresence>

                            {active && !collapsed && (
                              <motion.div
                                layoutId="activeIndicator"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-[20px] bg-white/40 rounded-r-full"
                              />
                            )}
                          </>
                        );
                      }}
                    </NavLink>
                  );

                  return (
                    <div key={item.path} className="mb-1">
                      {collapsed && !isMobile ? (
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger asChild>{linkItem}</TooltipTrigger>
                          <TooltipContent side="right" className="bg-sidebar-accent border-sidebar-border text-sidebar-foreground text-xs shadow-2xl">
                            {item.label}
                          </TooltipContent>
                        </Tooltip>
                      ) : linkItem}

                      <AnimatePresence>
                        {hasSubmenu && (!collapsed || isMobile) && isExpanded && (
                          <motion.div
                            initial="hidden"
                            animate="visible"
                            exit="hidden"
                            variants={{
                              hidden: { height: 0, opacity: 0 },
                              visible: {
                                height: "auto",
                                opacity: 1,
                                transition: {
                                  height: { duration: 0 },
                                  opacity: { duration: 0 },
                                  staggerChildren: 0,
                                },
                              },
                            }}
                            className="overflow-hidden ml-4 border-l border-sidebar-border/30"
                          >
                            <div className="space-y-1 pl-2 pb-2 mt-1">
                              {item.subItems!.map((sub) => {
                                const SubIcon = resolveIcon(sub.icon);
                                return (
                                  <motion.div
                                    key={sub.path}
                                    variants={{
                                      hidden: { x: -10, opacity: 0 },
                                      visible: { x: 0, opacity: 1 },
                                    }}
                                    transition={springTransition}
                                  >
                                    <NavLink
                                      to={sub.path}
                                      onClick={() => isMobile && onClose()}
                                      className={({ isActive: subActive }) => cn(
                                        "flex items-center w-full justify-start rounded-r-xl text-[12px] h-9 px-4 hover:translate-x-1 group/sub whitespace-nowrap ml-1 transition-all duration-150",
                                        subActive
                                          ? "text-violet-600 dark:text-violet-400 bg-violet-500/10 dark:bg-violet-500/20 font-bold border-l-[3px] border-violet-500 shadow-sm"
                                          : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-violet-500 font-normal"
                                      )}
                                    >
                                      {({ isActive: subActive }) => (
                                        <>
                                            <SubIcon className={cn(
                                              "h-4 w-4 mr-3 shrink-0 stroke-[2px]",
                                              subActive ? "text-violet-600 dark:text-violet-400" : "text-sidebar-foreground/70 group-hover/sub:text-violet-500"
                                            )} />
                                            <span className="truncate">{sub.label}</span>
                                        </>
                                      )}
                                    </NavLink>
                                  </motion.div>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })
              )}
            </nav>
          </TooltipProvider>
        </ScrollArea>

        <div className="p-4 border-t border-sidebar-border text-[10px] text-sidebar-foreground/40 font-normal text-center uppercase tracking-widest bg-transparent">
          © 2026 XEROALPHA
        </div>
      </motion.aside>
    </>
  );
}